import { checkIsLoop, checkIsLoopEnd } from "@/core/util";
import { getLoopEndOrigId, getLoopStartOrigId } from "@/flow/utils/loopUtils";
import { parseUiData, stringifyUiData } from "@/flow/utils/uiDataUtils";

type LoopMembership = { start: string; end: string | null; local: boolean };

type LoopCollection = {
  start: string;
  end: string | null;
  loopStartStep: any;
  collected: string[];
};

type LoopEdge = {
  source?: any;
  target?: any;
  sourceHandle?: any;
  targetHandle?: any;
  hidden?: boolean;
  data?: any;
  type?: string;
};

function isRouterStep(step: any): boolean {
  return step?.type === "ROUTER" || Array.isArray(step?.routes);
}

function updateUiData(step: any, mutator: (data: any) => void): void {
  if (!step) return;
  const current = typeof step.uiData === "string" ? step.uiData : undefined;
  const data = parseUiData(current) ?? {};
  mutator(data);

  if (Object.keys(data).length === 0) {
    if (current !== undefined) {
      delete step.uiData;
    }
    return;
  }

  const next = stringifyUiData(data);
  if (next !== current) {
    step.uiData = next;
  }
}

function clearKeys(obj: any, keys: string[]): void {
  if (!obj) return;
  keys.forEach((key) => {
    if (key in obj) {
      delete obj[key];
    }
  });
}

const LOOP_STEP_KEYS = ["loopStep", "loopStartOrigId", "loopStepOrigId"];
const ROUTER_LOOP_KEYS = ["insideLoop", "parentLoopOrigId", "loopEndOrigId"];

function applyLoopMembership(step: any, parent: LoopMembership | null): void {
  const isLoopStart = checkIsLoop(step) || step?.type === "LOOP";
  const isLoopEnd = checkIsLoopEnd(step) || step?.type === "LOOP_END";

  if (parent) {
    step.insideLoop = true;
    step.parentLoopOrigId = parent.start;
    if (parent.end) {
      step.loopEndOrigId = parent.end;
    }

    if (isRouterStep(step)) {
      updateUiData(step, (data) => {
        clearKeys(data, LOOP_STEP_KEYS);
        if (parent.local) {
          data.insideLoop = true;
          data.parentLoopOrigId = parent.start;
          if (parent.end) {
            data.loopEndOrigId = parent.end;
          }
        } else {
          clearKeys(data, ROUTER_LOOP_KEYS);
        }
      });
      return;
    }

    if (isLoopStart) {
      updateUiData(step, (data) => {
        if (parent.local) {
          data.loopStep = true;
          data.loopStartOrigId = parent.start;
        } else {
          delete data.loopStep;
          delete data.loopStartOrigId;
          delete data.loopStepOrigId;
        }
      });
      return;
    }

    if (!isLoopEnd) {
      updateUiData(step, (data) => {
        clearKeys(data, ROUTER_LOOP_KEYS);
        if (parent.local) {
          data.loopStep = true;
          data.loopStartOrigId = parent.start;
        } else {
          delete data.loopStep;
          delete data.loopStartOrigId;
          delete data.loopStepOrigId;
        }
      });
    }

    return;
  }

  // Not inside any loop: clear stale loop context.
  if ("insideLoop" in (step ?? {})) {
    delete step.insideLoop;
  }
  if ("parentLoopOrigId" in (step ?? {})) {
    delete step.parentLoopOrigId;
  }
  if ("loopEndOrigId" in (step ?? {})) {
    delete step.loopEndOrigId;
  }

  if (isRouterStep(step)) {
    updateUiData(step, (data) => {
      clearKeys(data, ROUTER_LOOP_KEYS);
      clearKeys(data, LOOP_STEP_KEYS);
    });
    return;
  }

  if (isLoopStart) {
    updateUiData(step, (data) => {
      delete data.loopStep;
      delete data.loopStartOrigId;
      delete data.loopStepOrigId;
    });
    return;
  }

  if (!isLoopEnd) {
    updateUiData(step, (data) => {
      delete data.loopStep;
      delete data.loopStartOrigId;
      delete data.loopStepOrigId;
    });
  }
}

function finalizeLoopCollection(ctx: LoopCollection): void {
  if (!ctx?.loopStartStep) return;
  updateUiData(ctx.loopStartStep, (data) => {
    if (ctx.end) {
      data.loopEndOrigId = ctx.end;
    }
    data.loop = true;
    data.steps = ctx.collected.map((origId, index) => ({
      loopStepIndex: index,
      origId,
    }));
  });
}

function normalizeId(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parseEdgeData(edge: LoopEdge): any {
  if (!edge?.data) return {};
  if (typeof edge.data === "object") return edge.data;

  try {
    return JSON.parse(edge.data);
  } catch {
    return {};
  }
}

function isHiddenEdge(edge: LoopEdge): boolean {
  return Boolean(edge?.hidden || parseEdgeData(edge).hidden);
}

function isLoopBackEdge(edge: LoopEdge): boolean {
  return (
    typeof edge?.sourceHandle === "string" &&
    typeof edge?.targetHandle === "string" &&
    edge.sourceHandle.includes("__handle-top") &&
    edge.targetHandle.includes("__handle-top")
  );
}

function isVisibleRightEdge(edge: LoopEdge): boolean {
  return Boolean(
    edge &&
      !isHiddenEdge(edge) &&
      edge.type !== "hidden" &&
      edge.type !== "serialized-hidden" &&
      !isLoopBackEdge(edge) &&
      typeof edge.sourceHandle === "string" &&
      edge.sourceHandle.includes("__handle-right")
  );
}

function collectStepsByOrigId(
  steps: any[],
  stepByOrigId = new Map<string, any>(),
  loops: any[] = []
): { stepByOrigId: Map<string, any>; loops: any[] } {
  if (!Array.isArray(steps)) return { stepByOrigId, loops };

  steps.forEach((step) => {
    const origId = normalizeId(step?.origId);
    if (origId && !stepByOrigId.has(origId)) {
      stepByOrigId.set(origId, step);
    }

    if (checkIsLoop(step) || step?.type === "LOOP") {
      loops.push(step);
    }

    if (isRouterStep(step) && Array.isArray(step.routes)) {
      step.routes.forEach((route: any) => {
        collectStepsByOrigId(route?.steps || [], stepByOrigId, loops);
      });
    }
  });

  return { stepByOrigId, loops };
}

function getConnectedLoopStepOrigIds(
  loopStep: any,
  edges: LoopEdge[]
): string[] | null {
  const loopOrigId = normalizeId(loopStep?.origId);
  const loopEndOrigId = getLoopEndOrigId(loopStep);
  if (!loopOrigId || !loopEndOrigId || !Array.isArray(edges)) return null;

  const collected: string[] = [];
  const visited = new Set<string>([loopOrigId]);
  let currentOrigId = loopOrigId;
  let hasLoopGraphEdge = false;

  while (currentOrigId) {
    const sourceOrigId = currentOrigId;
    const nextEdge = edges.find(
      (edge) => isVisibleRightEdge(edge) && edge.source === sourceOrigId
    );
    const nextOrigId = normalizeId(nextEdge?.target);

    if (!nextOrigId || visited.has(nextOrigId)) break;
    hasLoopGraphEdge = true;
    visited.add(nextOrigId);

    if (nextOrigId === loopEndOrigId) break;

    collected.push(nextOrigId);
    currentOrigId = nextOrigId;
  }

  return hasLoopGraphEdge ? collected : null;
}

function syncRouterRouteMembership(router: any, parent: LoopMembership): void {
  if (!isRouterStep(router) || !Array.isArray(router.routes)) return;

  const inheritedMembership = [
    {
      start: parent.start,
      end: parent.end,
      local: false,
    },
  ];

  router.routes.forEach((route: any) => {
    syncSteps(route?.steps || [], inheritedMembership);
  });
}

function clearLoopMembershipForStep(step: any, loopOrigId: string): void {
  if (!step) return;

  if (step.parentLoopOrigId === loopOrigId) {
    delete step.insideLoop;
    delete step.parentLoopOrigId;
    delete step.loopEndOrigId;
  }

  updateUiData(step, (data) => {
    if (
      data.loopStartOrigId === loopOrigId ||
      data.loopStepOrigId === loopOrigId
    ) {
      clearKeys(data, LOOP_STEP_KEYS);
    }

    if (data.parentLoopOrigId === loopOrigId) {
      clearKeys(data, ROUTER_LOOP_KEYS);
    }
  });

  if (isRouterStep(step) && Array.isArray(step.routes)) {
    step.routes.forEach((route: any) => {
      if (Array.isArray(route?.steps)) {
        route.steps.forEach((routeStep: any) => {
          clearLoopMembershipForStep(routeStep, loopOrigId);
        });
      }
    });
  }
}

function clearStaleLoopMembership({
  stepByOrigId,
  loopOrigId,
  loopEndOrigId,
  connectedStepOrigIds,
}: {
  stepByOrigId: Map<string, any>;
  loopOrigId: string;
  loopEndOrigId: string | null;
  connectedStepOrigIds: Set<string>;
}): void {
  stepByOrigId.forEach((step, origId) => {
    if (origId === loopOrigId || origId === loopEndOrigId) return;
    if (connectedStepOrigIds.has(origId)) return;

    clearLoopMembershipForStep(step, loopOrigId);
  });
}

function syncLoopUiDataFromEdges(stepList: any[], edges: LoopEdge[]): void {
  if (!Array.isArray(edges) || edges.length === 0) return;

  const { stepByOrigId, loops } = collectStepsByOrigId(stepList);

  loops.forEach((loopStep) => {
    const loopOrigId = normalizeId(loopStep?.origId);
    if (!loopOrigId) return;

    const connectedStepOrigIds = getConnectedLoopStepOrigIds(loopStep, edges);
    if (!connectedStepOrigIds) return;

    const loopEndOrigId = getLoopEndOrigId(loopStep);
    const connectedStepOrigIdSet = new Set(connectedStepOrigIds);
    const membership = {
      start: loopOrigId,
      end: loopEndOrigId,
      local: true,
    };

    clearStaleLoopMembership({
      stepByOrigId,
      loopOrigId,
      loopEndOrigId,
      connectedStepOrigIds: connectedStepOrigIdSet,
    });

    finalizeLoopCollection({
      start: loopOrigId,
      end: loopEndOrigId,
      loopStartStep: loopStep,
      collected: connectedStepOrigIds,
    });

    connectedStepOrigIds.forEach((origId) => {
      const step = stepByOrigId.get(origId);
      if (!step) return;

      applyLoopMembership(step, membership);
      syncRouterRouteMembership(step, membership);
    });
  });
}

function popMembershipForLoopEnd(
  membership: LoopMembership[],
  loopStartOrigId: string | null,
  loopEndOrigId: string
): void {
  if (!membership.length) return;
  const findIndex = () => {
    if (loopStartOrigId) {
      for (let i = membership.length - 1; i >= 0; i -= 1) {
        if (membership[i].start === loopStartOrigId) return i;
      }
    }
    for (let i = membership.length - 1; i >= 0; i -= 1) {
      if (membership[i].end === loopEndOrigId) return i;
    }
    return -1;
  };

  const idx = findIndex();
  if (idx === -1) return;
  membership.splice(idx);
}

function popCollectionsForLoopEnd(
  collections: LoopCollection[],
  loopStartOrigId: string | null,
  loopEndOrigId: string
): void {
  if (!collections.length) return;

  const findIndex = () => {
    if (loopStartOrigId) {
      for (let i = collections.length - 1; i >= 0; i -= 1) {
        if (collections[i].start === loopStartOrigId) return i;
      }
    }
    for (let i = collections.length - 1; i >= 0; i -= 1) {
      if (collections[i].end === loopEndOrigId) return i;
    }
    return -1;
  };

  const idx = findIndex();
  if (idx === -1) return;

  for (let i = collections.length - 1; i >= idx; i -= 1) {
    const ctx = collections.pop();
    if (ctx) {
      finalizeLoopCollection(ctx);
    }
  }
}

function syncSteps(
  steps: any[],
  inheritedMembership: LoopMembership[] = []
): void {
  if (!Array.isArray(steps) || steps.length === 0) return;

  const membership: LoopMembership[] = inheritedMembership.map((m) => ({
    start: m.start,
    end: m.end,
    local: false,
  }));
  const collections: LoopCollection[] = [];

  steps.forEach((step) => {
    if (!step) return;
    const origId = typeof step.origId === "string" ? step.origId : null;

    const parent = membership.length ? membership[membership.length - 1] : null;
    applyLoopMembership(step, parent);

    if (origId) {
      collections.forEach((ctx) => {
        if (ctx?.end && ctx.end === origId) return;
        ctx.collected.push(origId);
      });
    }

    const isLoopStart = checkIsLoop(step) || step?.type === "LOOP";
    if (isLoopStart && origId) {
      const end = getLoopEndOrigId(step);
      membership.push({ start: origId, end, local: true });
      collections.push({
        start: origId,
        end,
        loopStartStep: step,
        collected: [],
      });
    }

    const isLoopEnd = checkIsLoopEnd(step) || step?.type === "LOOP_END";
    if (isLoopEnd && origId) {
      const loopStartOrigId = getLoopStartOrigId(step);
      popCollectionsForLoopEnd(collections, loopStartOrigId, origId);
      popMembershipForLoopEnd(membership, loopStartOrigId, origId);
    }

    if (isRouterStep(step) && Array.isArray(step.routes)) {
      const inheritedForRoutes = membership.map((m) => ({
        start: m.start,
        end: m.end,
        local: false,
      }));
      step.routes.forEach((route: any) => {
        if (Array.isArray(route?.steps)) {
          syncSteps(route.steps, inheritedForRoutes);
        }
      });
    }
  });

  // Finalize any loops that didn't close cleanly.
  collections.forEach((ctx) => finalizeLoopCollection(ctx));
}

/**
 * Syncs loop-related uiData and helper fields with the current stepList order.
 *
 * Why: attach/detach operations can leave stale loop uiData behind, which can
 * cause loop outputs to "leak" outside LOOP_END (and across nested loops).
 *
 * Goals:
 * - Keep each LOOP step's `uiData.steps` consistent with the actual steps between LOOP and LOOP_END.
 * - Remove stale loop membership from steps moved outside a loop.
 * - Ensure nested loop and router-in-loop metadata stays consistent after attach/detach.
 */
export function syncLoopUiData(stepList: any[], edges: LoopEdge[] = []): void {
  syncSteps(stepList, []);
  syncLoopUiDataFromEdges(stepList, edges);
}
